<template>
    <v-container fluid>
        <ApolloQuery
            :query="require('@/graphql/user/allUsers.gql')"
            :variables="query_vars"
        >
            <template v-slot="{ result: { loading, error, data } }">
                <v-progress-linear indeterminate v-if="loading" />
                <v-alert
                    type="warning"
                    v-if="hasUpdatedProjectNames"
                    dismissible
                >
                    Due to the user deletion, you now own and/or manage the
                    following projects: <br />
                    <strong>{{ updatedProjectNames }}</strong>
                </v-alert>
                <v-data-table
                    v-if="data && !loading"
                    v-model="selected"
                    :items="filterUsers(data.allUsers)"
                    :headers="headers"
                    item-key="id"
                    :search="keywords"
                    :items-per-page.sync="itemsPerPage"
                    show-select
                    :footer-props="{ itemsPerPageOptions }"
                >
                    <template v-slot:item.user_type="{ item }">
                        {{ mapUserType(item.user_type) }}
                    </template>
                    <template v-slot:item.last_login="{ item }">
                        {{ item.last_login ? formatDate(item.last_login) : '-' }}
                    </template>
                    <template v-slot:item.last_location="{ item }">
                        {{ item.last_location ?? '-' }}
                    </template>
                    <template v-slot:item.action="{ item }">
                        <v-icon
                            class="mr-2"
                            v-if="
                                isSuperAdmin &&
                                    !hasDelegatedUser &&
                                    item.switch_user &&
                                    item.user_type !== 'admin'
                            "
                            @click="promptToSwitchUser(item)"
                            style="color: #ffa500;"
                        >
                            mdi-account-switch
                        </v-icon>
                        <v-icon class="mr-2" @click="editUser(item)">
                            mdi-pencil
                        </v-icon>
                        <v-icon small @click="promptForDelete(item)">
                            mdi-delete
                        </v-icon>
                    </template>
                </v-data-table>
                <v-alert type="error" v-else-if="error">
                    There was an error loading data. The server said...<br />
                    <strong>{{ error }}</strong>
                </v-alert>
            </template>
        </ApolloQuery>
        <v-dialog
            v-model="deleteDialog"
            persistent
            max-width="320"
            v-if="selectedUser.name != ''"
        >
            <v-card>
                <v-card-title class="headline">Delete User?</v-card-title>
                <v-card-text>
                    <p>
                        You are about to delete the user
                        <strong>{{ selectedUser.name }}</strong
                        >. This cannot be undone. Are you sure you want to
                        delete this user?
                    </p>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn
                        color="red darken-1"
                        text
                        @click="deleteDialog = false"
                        >{{ $t('cancel') }}</v-btn
                    >
                    <v-btn
                        color="primary darken-1"
                        text
                        @click="deleteUser(selectedUser)"
                        >{{ $t('delete') }}</v-btn
                    >
                </v-card-actions>
            </v-card>
        </v-dialog>
        <v-dialog
            v-model="switchUserDialog"
            persistent
            max-width="320"
            v-if="selectedUser.name != ''"
        >
            <v-card>
                <v-card-title class="headline">Switch User?</v-card-title>
                <v-card-text>
                    <p>
                        You are about to switch to the user
                        <strong>{{ selectedUser.name }}</strong
                        >. Your super admin access may be impacted but you can
                        revert back. Are you sure you want to switch to this
                        user?
                    </p>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn
                        color="red darken-1"
                        text
                        @click="switchUserDialog = false"
                        >{{ $t('cancel') }}</v-btn
                    >
                    <v-btn
                        color="primary darken-1"
                        text
                        @click="switchUser(selectedUser)"
                        >Switch User</v-btn
                    >
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-container>
</template>

<script>
import axios from 'axios'
import { mapGetters } from 'vuex'
import { getInstance } from '../../auth/authWrapper'
import moment from 'moment'

export default {
    name: 'UsersGrid',
    components: {
        // "user-card": UserCard
    },
    props: {
        keywords: {
            type: String,
            required: true,
        },
        reload: {
            type: Number,
            default: 0,
        },
        client_id: {
            type: String,
        },
        user_type: {
            type: String,
        },
        last_login: {
            type: Array,
        },
        showSwitchUsers: {
            type: Boolean,
        },
    },
    data: () => ({
        itemsPerPageOptions: [25, 50, 100],
        itemsPerPage: 25,
        deleteDialog: false,
        switchUserDialog: false,
        hasUpdatedProjectNames: false,
        updatedProjectNames: '',
        selectedUser: {},
        selected: [],
        rando: 0,
        headers: [
            {
                text: 'Name',
                align: 'left',
                value: 'name',
            },
            { text: 'Email', value: 'email' },
            { text: 'Type', value: 'user_type' },
            { text: 'Client', value: 'client.name' },
            { text: 'Last Login', value: 'last_login' },
            { text: 'Last Location', value: 'last_location' },
            {
                text: 'Actions',
                align: 'right',
                sortable: false,
                value: 'action',
            },
        ],
    }),
    watch: {
        reload: function(val) {
            this.rando = val
        },
    },
    computed: {
        ...mapGetters({
            delegatedUser: 'delegatedUser',
        }),
        query_vars: function() {
            if (this.$store.getters.userForPortalAccess.user_type === 'admin') {
                return { garbage: this.rando }
            } else {
                return { client_id: this.client_id, garbage: this.rando }
            }
        },
        isSuperAdmin() {
            return this.$store.getters.userForPortalAccess.user_type === 'admin'
        },
        hasDelegatedUser() {
            return this.delegatedUser != null
        },
        shouldEnableSwitchUser(userItem) {
            return (
                this.isSuperAdmin() &&
                !this.hasDelegatedUser &&
                userItem.switch_user &&
                userItem.user_type !== 'admin'
            )
        },
    },
    methods: {
        formatDate(date) {
            return moment(date, 'YYYY-MM-DD HH:mm:ss').format(
                'MM/DD/YYYY hh:mm A'
            )
        },
        filterUserType(userItem) {
            if (this.user_type) {
                return this.user_type === userItem.user_type
            }

            return true
        },
        filterClientId(userItem) {
            if (this.client_id) {
                if (this.client_id === '0') {
                    return ! userItem.client
                } else if (userItem.client) {
                    return this.client_id === userItem.client.id
                }

                return false
            }

            return true
        },
        filterSwitchable(userItem) {
            if (this.showSwitchUsers) {
                return userItem.switch_user && userItem.user_type !== 'admin'
            }

            return true;
        },
        filterLastLogin(userItem) {
            const lastLogin = userItem.last_login ? moment(userItem.last_login).format('YYYY-MM-DD').valueOf() : null

            if (this.last_login.length === 0) {
                return true
            } else if (lastLogin === null) {
                return false
            } else if (this.last_login.length === 1) {
                return lastLogin === this.last_login[0]
            }

            return (lastLogin >= this.last_login[0]) && (lastLogin <= this.last_login[1])
        },
        filterUsers(users) {
            return users
                .filter(this.filterUserType)
                .filter(this.filterClientId)
                .filter(this.filterSwitchable)
                .filter(this.filterLastLogin)
        },
        mapUserType(type) {
            if (type === 'admin') {
                return 'Administrator'
            } else if (type === 'client_admin') {
                return 'Company Administrator'
            } else {
                return 'Company User'
            }
        },
        promptForDelete(user) {
            this.selectedUser = user
            this.deleteDialog = true
        },
        promptToSwitchUser(user) {
            this.selectedUser = user
            this.switchUserDialog = true
        },

        async deleteUser(user) {
            const rando = this.rando;

            this.$apollo
                .mutate({
                    // Query
                    mutation: require('@/graphql/user/deleteUser.gql'),
                    // Parameters
                    variables: {
                        id: user.id,
                    },

                    // Updates the user list without making a new query
                    update(cache, {data: {deleteUser}}) {
                        if (deleteUser.success) {
                            // Current users
                            const currentUsers = cache.readQuery({
                                query: require('@/graphql/user/allUsers.gql'),
                                variables: { garbage: rando },
                            });

                            // Remove deleted user from the list
                            const filteredUsers = currentUsers.allUsers.filter((listedUser) => listedUser.id !== user.id);

                            // Updates the cached list
                            cache.writeQuery({
                                query: require('@/graphql/user/allUsers.gql'),
                                variables: { garbage: rando },
                                data: { allUsers: filteredUsers },
                            });
                        }
                    },
                })
                .then(({data}) => {
                    if (data.deleteUser.success) {
                        this.hasUpdatedProjectNames = data.deleteUser.projects.length > 0;
                        this.updatedProjectNames = data.deleteUser.projects.join(', ');
                    } else {
                        this.hasUpdatedProjectNames = false;
                        this.updatedProjectNames = '';
                    }

                    // report success
                    this.$emit('user-form-closed', data.deleteUser)
                })
                // eslint-disable-next-line no-unused-vars
                .catch(error => {
                    // Error
                    // update user....
                })
            this.deleteDialog = false
        },
        async switchUser(user) {
            // assign a delegated user
            console.log(
                `switching to user ${user.name} with type ${user.user_type}`
            )
            const userResponse = await this.$apollo.query({
                query: require('@/graphql/user/user.gql'),
                variables: { id: user.id },
            })
            const authService = getInstance()
            await authService.updateDelegatedUser(userResponse.data.user)
            await axios
                .get(process.env.VUE_APP_API_ENDPOINT + 'loginById/' + user.id)
                .then(response => {
                    console.log(`loginById response ${response}`)
                })
                .catch(console.error)
            // reload main page
            setTimeout(async () => {
                await this.$auth.loginWithRedirect()
            }, 100)
        },
        editUser(user) {
            this.$emit('edit-user', user)
        },
    },
}
</script>
